import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useCursorGlow } from '../hooks/useCursorGlow';
import fetchWithRetry from '../utils/fetchWithRetry';
import SignUpTour from './SignUpTour';

const API_BASE = process.env.REACT_APP_API_URL ||
  (window.location.port === '3000' ? `http://${window.location.hostname}:3001` : '');

export default function Login({ onAuth }) {
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  useCursorGlow(containerRef);

  useEffect(() => {
    if (cardRef.current) cardRef.current.classList.add('mppc-active');
  }, []);

  const [isRegister, setIsRegister] = useState(false);
  const [showSignUpTour, setShowSignUpTour] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [resetStep, setResetStep] = useState('request');
  const [resetToken, setResetToken] = useState('');
  const [resetTokenFromUrl, setResetTokenFromUrl] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Auto-detect reset token or signup action from URL query params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const urlToken = params.get('reset_token');
    if (urlToken) {
      setIsReset(true);
      setResetStep('confirm');
      setResetToken(urlToken);
      setResetTokenFromUrl(true);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    // ?signup=true → open signup tour (for funnel links)
    if (params.get('signup') === 'true') {
      localStorage.removeItem('signUpTourSeen');
      setShowSignUpTour(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleSignUpClick = useCallback(() => {
    const seen = localStorage.getItem('signUpTourSeen') === 'true';
    if (seen) {
      setIsRegister(true);
      setError('');
    } else {
      setShowSignUpTour(true);
    }
  }, []);

  const handleTourContinue = useCallback(() => {
    localStorage.setItem('signUpTourSeen', 'true');
    setShowSignUpTour(false);
    setIsRegister(true);
    setError('');
  }, []);

  const handleTourSignIn = useCallback(() => {
    setShowSignUpTour(false);
    setIsRegister(false);
    setError('');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    if (isReset) {
      if (resetStep === 'request') {
        try {
          const res = await fetchWithRetry(`${API_BASE}/api/auth/request-reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          const data = await res.json().catch(() => ({}));
          setMessage(data.message || 'If that email exists, a password reset link has been sent.');
          setResetStep('confirm');
        } catch (err) {
          setError(`Could not connect to server: ${err.message}`);
        }
        setLoading(false);
        return;
      }
      if (newPassword.length < 8) {
        setError('Password must be at least 8 characters');
        setLoading(false);
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }
      try {
        const res = await fetchWithRetry(`${API_BASE}/api/auth/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: resetToken, newPassword }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { setError(data.error || `Reset failed (${res.status})`); setLoading(false); return; }
        setMessage(data.message);
        setTimeout(() => { setIsReset(false); setResetStep('request'); setResetToken(''); setResetTokenFromUrl(false); setNewPassword(''); setConfirmPassword(''); setMessage(''); }, 2000);
      } catch (err) {
        setError(`Could not connect to server: ${err.message}`);
      }
      setLoading(false);
      return;
    }

    const endpoint = isRegister ? '/api/auth/register' : '/api/auth/login';
    const body = isRegister ? { email, password, name } : { email, password };

    try {
      const res = await fetchWithRetry(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.error || `Something went wrong (${res.status})`);
        setLoading(false);
        return;
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onAuth(data.token, data.user);
    } catch (err) {
      setError(`Could not connect to server: ${err.message}`);
    }
    setLoading(false);
  };

  return (
    <div className="login-container" ref={containerRef}>
      <div className="login-card" ref={cardRef}>
        <div className="login-brand">More Profit Per Call</div>
        <h2 className="login-title">
          {isReset ? 'Reset Password' : isRegister ? 'Create Account' : 'Sign In'}
        </h2>
        <p className="login-subtitle">Real-Time AI Call Coaching</p>

        <form onSubmit={handleSubmit} className="login-form">
          {isReset ? (
            resetStep === 'request' ? (
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="login-input"
                required
              />
            ) : (
              <>
                {resetTokenFromUrl ? (
                  <div className="login-info">Reset token verified. Choose your new password.</div>
                ) : (
                  <input
                    type="text"
                    placeholder="Reset token (from email)"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    className="login-input"
                    required
                  />
                )}
                <input
                  type="password"
                  placeholder="New password (min 8 chars)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="login-input"
                  required
                  minLength={8}
                />
                <input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="login-input"
                  required
                  minLength={8}
                />
              </>
            )
          ) : (
            <>
              {isRegister && (
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="login-input"
                />
              )}
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="login-input"
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="login-input"
                required
                minLength={isRegister ? 8 : 6}
              />
            </>
          )}

          {error && <div className="login-error">{error}</div>}
          {message && <div className="login-error" style={{ color: '#34d399' }}>{message}</div>}

          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? 'Please wait...' : isReset ? (resetStep === 'request' ? 'Send Reset Request' : 'Reset Password') : isRegister ? 'Create Account' : 'Sign In'}
          </button>
        </form>

        <div className="login-toggle">
          {isReset ? (
            <button type="button" className="login-toggle-btn" onClick={() => { setIsReset(false); setResetStep('request'); setResetToken(''); setResetTokenFromUrl(false); setNewPassword(''); setConfirmPassword(''); setError(''); setMessage(''); }}>
              Back to Sign In
            </button>
          ) : (
            <>
              {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button type="button" className="login-toggle-btn" onClick={isRegister ? () => { setIsRegister(false); setError(''); } : handleSignUpClick}>
                {isRegister ? 'Sign In' : 'Sign Up'}
              </button>
              {!isRegister && (
                <>
                  {' · '}
                  <button type="button" className="login-toggle-btn" onClick={() => { setIsReset(true); setError(''); }}>
                    Forgot Password?
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
      {showSignUpTour && (
        <SignUpTour onContinue={handleTourContinue} onSignIn={handleTourSignIn} />
      )}
    </div>
  );
}
